// src/services/leaderboardService.js
//
// LEADERBOARDS (step 4) — the browser side. Read-only: the boards are
// written by functions/coins.js every time coins are earned (games, first
// video / song / sing-along, treasure chests — not teacher gifts or shop
// purchases), so nobody can type their way to the top.
//
//   weekKey(date)            Monday (New York time) that starts the week
//   useClassBoard(code)      { rows, lastWeek, teamId, loaded } for the class
//   useLeagueBoard()         { rows, loaded } — all-schools board
//   useLeagueSetting(code)   { on, team, loaded } — teacher's switch
//   setLeagueOptIn(on, team) teacher switches all-schools on / off
//   msToReset()              ms until Monday 00:00 New York

import { useEffect, useState } from "react";
import { doc, onSnapshot, getDoc } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "../firebase/firebaseConfig";

const NY = "America/New_York";
const pad = (n) => String(n).padStart(2, "0");

/** The date/time on the New York clock, as a plain Date (its fields read NY time). */
const nyNow = (date = new Date()) => new Date(date.toLocaleString("en-US", { timeZone: NY }));

export const weekKey = (date = new Date()) => {
  const ny = nyNow(date);
  ny.setDate(ny.getDate() - ((ny.getDay() + 6) % 7));
  return `${ny.getFullYear()}-${pad(ny.getMonth() + 1)}-${pad(ny.getDate())}`;
};

export const lastWeekKey = () => weekKey(new Date(Date.now() - 7 * 864e5));

export const msToReset = () => {
  const ny = nyNow();
  const next = new Date(ny);
  next.setDate(ny.getDate() + (7 - ((ny.getDay() + 6) % 7)));
  next.setHours(0, 0, 0, 0);
  return Math.max(0, next - ny);
};

const rank = (list) => {
  const sorted = list.filter((r) => r.coins > 0).sort((a, b) => b.coins - a.coins || String(a.name).localeCompare(String(b.name)));
  // ties share a place: 1, 2, 2, 4
  let place = 0;
  return sorted.map((r, i) => {
    if (i === 0 || r.coins !== sorted[i - 1].coins) place = i + 1;
    return { ...r, place };
  });
};

const entriesToRows = (data) =>
  rank(Object.entries((data && data.entries) || {}).map(([uid, e]) => ({ id: uid, uid, name: e.name || "Player", coins: Number(e.coins) || 0 })));

export const useClassBoard = (code) => {
  const [state, setState] = useState({ rows: [], lastWeek: [], teamId: "", loaded: false, error: "" });
  useEffect(() => {
    if (!code) {
      setState({ rows: [], lastWeek: [], teamId: "", loaded: true, error: "" });
      return undefined;
    }
    let alive = true;
    getDoc(doc(db, "classBoards", code, "weeks", lastWeekKey()))
      .then((s) => alive && setState((p) => ({ ...p, lastWeek: entriesToRows(s.exists() ? s.data() : null) })))
      .catch(() => {});
    const stop = onSnapshot(
      doc(db, "classBoards", code, "weeks", weekKey()),
      (s) =>
        alive &&
        setState((p) => ({ ...p, rows: entriesToRows(s.exists() ? s.data() : null), teamId: (s.exists() && s.data().teamId) || p.teamId, loaded: true, error: "" })),
      (err) => alive && setState((p) => ({ ...p, loaded: true, error: err?.code || "error" }))
    );
    return () => {
      alive = false;
      stop();
    };
  }, [code]);
  return state;
};

export const useLeagueBoard = (enabled = true) => {
  const [state, setState] = useState({ rows: [], loaded: false });
  useEffect(() => {
    if (!enabled) return undefined;
    const stop = onSnapshot(
      doc(db, "leagueBoards", weekKey()),
      (s) => {
        const classes = (s.exists() && s.data().classes) || {};
        setState({
          rows: rank(Object.entries(classes).map(([id, c]) => ({ id, name: c.team || "A class", state: c.state || "", grade: c.grade || "", coins: Number(c.coins) || 0 }))),
          loaded: true,
        });
      },
      () => setState({ rows: [], loaded: true })
    );
    return stop;
  }, [enabled]);
  return state;
};

export const useLeagueSetting = (code) => {
  const [state, setState] = useState({ on: false, team: "", teamId: "", loaded: false });
  useEffect(() => {
    if (!code) return undefined;
    return onSnapshot(
      doc(db, "educatorDirectory", code),
      (s) => {
        const l = (s.exists() && s.data().league) || {};
        setState((p) => ({ ...p, on: Boolean(l.on), team: l.team || "", loaded: true }));
      },
      () => setState((p) => ({ ...p, loaded: true }))
    );
  }, [code]);
  return state;
};

export const setLeagueOptIn = async (on, team) => {
  const res = await httpsCallable(functions, "setLeagueOptIn")({ on, team });
  return res?.data || null;
};

/** "2 days 5 hours" / "5 hours" / "12 minutes" */
export const resetLabel = (ms = msToReset()) => {
  const m = Math.floor(ms / 60000);
  const d = Math.floor(m / 1440);
  const h = Math.floor((m % 1440) / 60);
  if (d > 0) return `${d} day${d === 1 ? "" : "s"}${h ? ` ${h} hour${h === 1 ? "" : "s"}` : ""}`;
  if (h > 0) return `${h} hour${h === 1 ? "" : "s"}`;
  return `${Math.max(1, m % 60)} minute${m % 60 === 1 ? "" : "s"}`;
};
